// TODO(ui-foundation): promote this component to ui-foundation and remove from this repo.
import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { eventsAdminUserServiceClient } from "@app/api/user-service-client";
import { AuthLinkButton } from "@app/components/atoms/auth-link-button";
import { AuthHeading } from "@app/components/molecules/auth-heading";
import { mapUserServiceError } from "@app/lib/auth/user-service-errors";
import {
  Button,
  ValidatedForm,
  ValidatedInput,
  createFormSchema,
  schemas,
  useValidatedForm,
} from "@app/lib/ui-foundation";

const signupSchema = createFormSchema({
  fullName: schemas.sanitizedRequiredString,
  email: schemas.email,
});

type SignupValues = {
  fullName: string;
  email: string;
};

export type SignupFormProps = {
  onSwitchToLogin?: () => void;
  headingAs?: "h1" | "h2";
  /**
   * Called after a successful signup that requires email verification.
   * When provided, the form delegates navigation to the parent (e.g. so a
   * modal host can close itself before routing to /verify-email).
   * When omitted, the form navigates to /verify-email itself.
   */
  onUnconfirmedSignup?: (args: { email: string }) => void;
  /**
   * Called when signup returns user_confirmed: true (no verification step).
   * When omitted, the form navigates to /login itself.
   */
  onPreConfirmedSignup?: () => void;
};

export function SignupForm({
  onSwitchToLogin,
  headingAs = "h2",
  onUnconfirmedSignup,
  onPreConfirmedSignup,
}: SignupFormProps) {
  const navigate = useNavigate();

  const [errorMessage, setErrorMessage] = useState("");
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const signupForm = useValidatedForm(signupSchema, {
    mode: "onChange",
    defaultValues: {
      fullName: "",
      email: "",
    },
  });

  const handleSignup = useCallback(
    async (values: SignupValues) => {
      setErrorMessage("");
      setErrorCode(null);
      try {
        const response = await eventsAdminUserServiceClient.signup({
          email: values.email,
          name: values.fullName,
        });

        if (response.user_confirmed) {
          // Rare path: account was pre-confirmed. Skip /verify-email and
          // direct the user to /login with a success banner.
          if (onPreConfirmedSignup) {
            onPreConfirmedSignup();
          } else {
            navigate("/login", {
              replace: true,
              state: { banner: "Account created \u2014 please sign in." },
            });
          }
          return;
        }

        // Standard path: route to /verify-email with email + password held
        // in location.state (in-memory only, never persisted, never in URL).
        if (onUnconfirmedSignup) {
          onUnconfirmedSignup({ email: values.email });
        } else {
          navigate("/verify-email", {
            state: { email: values.email },
          });
        }
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error("[signup] submit failed", error);
        }
        const mapped = mapUserServiceError(error);
        setErrorMessage(mapped.message);
        setErrorCode(mapped.code);
      }
    },
    [navigate, onPreConfirmedSignup, onUnconfirmedSignup],
  );

  return (
    <>
      <AuthHeading
        as={headingAs}
        title="Create your account"
        subtitle="Start organizing your travel today."
      />

      <ValidatedForm
        form={signupForm}
        onSubmit={handleSignup}
        className="events-form-stack"
      >
        <ValidatedInput
          form={signupForm}
          name="fullName"
          label="Full Name"
          type="text"
          placeholder="Your full name"
          autoComplete="name"
        />
        <ValidatedInput
          form={signupForm}
          name="email"
          label="Email Address"
          type="email"
          placeholder="you@company.com"
          autoComplete="email"
        />

        <p className="events-auth-card__error" role="alert" aria-live="polite">
          {errorMessage}
          {errorCode === "USER_EXISTS" && onSwitchToLogin ? (
            <>
              {" "}
              <AuthLinkButton onClick={onSwitchToLogin}>Login</AuthLinkButton>
            </>
          ) : null}
        </p>

        <Button
          size="lg"
          color="primary"
          className="events-solid-button"
          type="submit"
          isDisabled={!signupForm.isValid || signupForm.isSubmitting}
        >
          Sign Up
        </Button>
      </ValidatedForm>

      {onSwitchToLogin ? (
        <div className="events-auth-card__footer">
          Already have an account?{" "}
          <AuthLinkButton onClick={onSwitchToLogin}>Login</AuthLinkButton>
        </div>
      ) : null}
    </>
  );
}
